import { db, storage } from '../firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  deleteDoc, 
  serverTimestamp,
  query,
  orderBy
} from 'firebase/firestore';
import { ref as storageRef, deleteObject } from 'firebase/storage';

/**
 * Service for managing custom uploaded assets in Firestore
 * Stores assets under workspace/{companyId}/customAssets collection
 * 
 * Asset structure:
 * - url: string (Firebase Storage URL)
 * - name: string (filename)
 * - category: string (player, collectible, background, etc.)
 * - uploadedAt: timestamp
 * - field: string (original field it was uploaded for)
 */

// Subscriptions retired: every workspace gets the same generous cap.
const MAX_ASSETS = 500;

class CustomAssetsService {
  
  /**
   * Get the custom assets collection reference for a company
   */
  getAssetsCollection(companyId) {
    return collection(db, 'workspace', companyId, 'customAssets');
  }

  /**
   * Get all custom assets for a company
   * @returns {Array} Array of asset objects
   */
  async getCompanyAssets(companyId) {
    try {
      const assetsRef = this.getAssetsCollection(companyId);
      const q = query(assetsRef, orderBy('uploadedAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const assets = [];
      snapshot.forEach(doc => {
        assets.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return assets;
    } catch (error) {
      console.error('Error fetching custom assets:', error);
      return [];
    }
  }

  /**
   * Get asset count for a company
   */
  async getAssetCount(companyId) {
    try {
      const assets = await this.getCompanyAssets(companyId);
      return assets.length;
    } catch (error) {
      console.error('Error counting assets:', error);
      return 0;
    }
  }

  /**
   * Check if company can add more assets
   */
  async canAddAsset(companyId) {
    const count = await this.getAssetCount(companyId);
    return count < this.getMaxAssets();
  }

  /**
   * Save a new custom asset
   * @returns {Object|null} The created asset or null if limit reached
   */
  async saveAsset(companyId, assetData) {
    try {
      // Check limit
      const maxAssets = this.getMaxAssets();
      const canAdd = await this.canAddAsset(companyId);
      if (!canAdd) {
        console.warn('Asset limit reached');
        return { error: 'limit_reached', message: `Maximum ${maxAssets} assets allowed. Delete some to add more.` };
      }

      // Check for duplicate URL
      const existingAssets = await this.getCompanyAssets(companyId);
      const isDuplicate = existingAssets.some(a => a.url === assetData.url);
      if (isDuplicate) {
        // Already exists, no need to add again
        return existingAssets.find(a => a.url === assetData.url);
      }

      const assetsRef = this.getAssetsCollection(companyId);
      
      const newAsset = {
        url: assetData.url,
        name: assetData.name || 'Custom Asset',
        category: assetData.category || 'collectible',
        field: assetData.field || null,
        uploadedAt: serverTimestamp()
      };

      const docRef = await addDoc(assetsRef, newAsset);
      
      return {
        id: docRef.id,
        ...newAsset
      };
    } catch (error) {
      console.error('Error saving custom asset:', error);
      throw error;
    }
  }

  /**
   * Delete a custom asset
   * Optionally also deletes the file from storage
   */
  async deleteAsset(companyId, assetId, deleteFromStorage = false, storageUrl = null) {
    try {
      // Delete from Firestore
      const assetRef = doc(db, 'workspace', companyId, 'customAssets', assetId);
      await deleteDoc(assetRef);

      // Optionally delete from Storage
      if (deleteFromStorage && storageUrl) {
        try {
          // Extract path from Firebase Storage URL
          const urlObj = new URL(storageUrl);
          const pathMatch = urlObj.pathname.match(/\/o\/(.+)\?/);
          if (pathMatch) {
            const storagePath = decodeURIComponent(pathMatch[1]);
            const fileRef = storageRef(storage, storagePath);
            await deleteObject(fileRef);
          }
        } catch (storageError) {
          // Log but don't fail if storage deletion fails
          console.warn('Could not delete file from storage:', storageError);
        }
      }

      return true;
    } catch (error) {
      console.error('Error deleting custom asset:', error);
      throw error;
    }
  }

  /**
   * Map field name to asset category
   */
  getFieldCategory(field) {
    const fieldCategoryMap = {
      // Player assets
      'playerImage': 'player',
      'birdImage': 'player',
      
      // Collectibles
      'foodImage': 'collectible',
      'cubeImage': 'collectible',
      'collectableImage': 'collectible',
      'collectibleImage': 'collectible',
      'powerUpImage': 'collectible',
      
      // Obstacles
      'obstacleImage': 'obstacle',
      
      // Backgrounds
      'backgroundImage': 'background',
      'backgroundImageFar': 'background',
      'backgroundImageMid': 'background',
      'backgroundImageNear': 'background',
      
      // Platforms
      'platformImageLeft': 'platform',
      'platformImageMiddle': 'platform',
      'platformImageRight': 'platform',
      
      // Other
      'coverPhoto': 'background',
      'sharingPhoto': 'background',
      'logo': 'collectible',
    };

    return fieldCategoryMap[field] || 'collectible';
  }

  /**
   * Get max assets limit
   */
  getMaxAssets() {
    return MAX_ASSETS;
  }
}

const customAssetsService = new CustomAssetsService();
export default customAssetsService;
